/**
 * Levels, challenges and the journal: the adapter between this game and the
 * progression engine.
 *
 * The engine is `src/progression/` and the content is
 * `src/content/progression.ts`. What lives here is only the adapter: which
 * game events move which counter, and how the game's records map onto the
 * journal's collections. `App` owns the records and hands them over through
 * `ProgressionHost` as functions, because it replaces its sets and its
 * profile wholesale and a captured reference would go stale.
 */

import { BADGES, type BadgeId } from '../content/badges';
import { CATEGORY_LABEL, FACTS, TOTAL_FACTS } from '../content/facts';
import {
  CHALLENGES,
  CHALLENGES_PER_WINDOW,
  JOURNAL_COPY,
  LEVEL_CURVE,
  LEVEL_REWARD_SEEDLINGS,
  RANKS,
  WEEK_STARTS_ON,
  XP_WEIGHTS,
  type Metric,
} from '../content/progression';
import { RARITY_LABEL, SPECIES, SPECIES_ORDER, type SpeciesId } from '../content/species';
import { MUTATIONS, WEATHER, WEATHER_ORDER, type MutationId } from '../content/weather';
import type { AudioEngine } from '../core/AudioEngine';
import type { SavedProfile } from '../core/Storage';
import type { Plant } from '../game/Plant';
import { formatDuration, recordVisit } from '../game/progress';
import {
  bump,
  claimable,
  levelFor,
  markDone,
  progressOf,
  rankFor,
  read,
  rollWindow,
  windowEndsAt,
  xpFrom,
  type Cadence,
  type Counters,
  type LevelInfo,
} from '../progression';
import type { CollectionSection, JournalChallengeGroup, JournalView } from '../ui/journal';
import { hex, speciesGlyph, type UI } from '../ui/UI';

export interface ProgressionHost {
  readonly ui: UI;
  readonly audio: AudioEngine;
  profile(): SavedProfile;
  /** True while a shared link is open. Nothing moves during a visit. */
  visiting(): boolean;
  factsSeen(): ReadonlySet<string>;
  speciesGrown(): ReadonlySet<SpeciesId>;
  mutations(): ReadonlySet<MutationId>;
  badges(): ReadonlySet<BadgeId>;
  persist(): void;
  refreshHud(): void;
}

export class ProgressionAdapter {
  private host: ProgressionHost;

  constructor(host: ProgressionHost) {
    this.host = host;
  }

  /** Move a stored counter. Never during a visit — nothing there is ours. */
  bump(metric: Metric, by = 1): void {
    if (this.host.visiting()) return;
    bump(this.host.profile().counters, metric, by);
  }

  /** The metrics a matured specimen can move beyond `matured` itself. */
  countSpecimen(plant: Plant): void {
    if (plant.mutation) this.bump('marked');
    const rarity = plant.species.rarity;
    if (rarity === 'rare' || rarity === 'legendary') this.bump('rare');
  }

  /** Log a day played, once per calendar day. */
  noteVisit(now: number): void {
    const profile = this.host.profile();
    const last = profile.days[profile.days.length - 1];
    profile.days = recordVisit(profile.days, now);
    if (profile.days[profile.days.length - 1] !== last) this.bump('days');
  }

  /**
   * Seed `days` from the visit log the first time this build runs, so a
   * returning player's history counts toward their level.
   */
  seedDays(): void {
    const profile = this.host.profile();
    profile.counters.days = Math.max(read(profile.counters, 'days'), profile.days.length);
  }

  /**
   * Every counter the engine can see.
   *
   * Only the metrics with no other home are stored as counters. The rest are
   * read straight off the records the game already keeps, so a lifetime tree
   * count cannot exist twice and disagree with itself.
   */
  private counters(): Counters<Metric> {
    const profile = this.host.profile();
    return {
      ...(profile.counters as Counters<Metric>),
      matured: profile.treesGrown,
      harvested: profile.harvested,
      facts: this.host.factsSeen().size,
      species: this.host.speciesGrown().size,
      mutations: this.host.mutations().size,
      badges: this.host.badges().size,
    };
  }

  private get levelInfo(): LevelInfo {
    return levelFor(xpFrom(this.counters(), XP_WEIGHTS), LEVEL_CURVE);
  }

  rollChallenges(now: number): void {
    const profile = this.host.profile();
    const counters = this.counters();
    for (const cadence of ['daily', 'weekly'] as const) {
      profile.challenges[cadence] = rollWindow(
        profile.challenges[cadence],
        CHALLENGES,
        cadence,
        now,
        counters,
        CHALLENGES_PER_WINDOW[cadence],
        WEEK_STARTS_ON,
      );
    }
  }

  /**
   * Claim finished challenges, pay out levels, and update the ring.
   *
   * Called after anything that can move a counter. Cheap: a few dozen integer
   * comparisons and one square root.
   */
  advance(): void {
    const { ui, audio } = this.host;
    if (this.host.visiting()) {
      // Nothing moves during a visit, but the ring is still the visitor's own.
      const info = this.levelInfo;
      ui.setLevel(info.level, info.progress);
      return;
    }
    this.rollChallenges(Date.now());
    const profile = this.host.profile();

    for (const cadence of ['daily', 'weekly'] as const) {
      const window = profile.challenges[cadence];
      if (!window) continue;
      const ready = claimable(window, CHALLENGES, this.counters());
      if (ready.length === 0) continue;
      profile.challenges[cadence] = markDone(window, ready.map((def) => def.id));
      for (const def of ready) {
        this.bump('challenges');
        this.bump('challengeXp', def.xp);
        ui.prize(
          JOURNAL_COPY.challengeDone(cadence),
          JOURNAL_COPY.challengeDoneBody(def.title, def.xp),
        );
      }
      audio.bloom(2);
      this.host.persist();
    }

    const info = this.levelInfo;
    if (info.level > profile.levelClaimed) {
      /*
       * Pay every level crossed, in one announcement. A save from before
       * levels existed arrives several levels up, and five toasts in a row
       * would bury the garden the player just came back to.
       */
      const paid = (info.level - profile.levelClaimed) * LEVEL_REWARD_SEEDLINGS;
      profile.levelClaimed = info.level;
      profile.seedlings += paid;
      const rank = rankFor(info.level, RANKS);
      ui.prize(JOURNAL_COPY.levelUp(info.level, rank.name), JOURNAL_COPY.levelUpBody(paid));
      audio.bloom(-3);
      this.host.refreshHud();
      this.host.persist();
    }

    ui.setLevel(info.level, info.progress);
    if (ui.journalOpen) ui.refreshJournal(this.journalView());
  }

  showJournal(): void {
    this.host.audio.tick();
    this.advance();
    this.host.ui.openJournal(this.journalView());
  }

  private journalView(): JournalView {
    const { ui } = this.host;
    const profile = this.host.profile();
    const factsSeen = this.host.factsSeen();
    const speciesGrown = this.host.speciesGrown();
    const mutations = this.host.mutations();
    const badges = this.host.badges();
    const now = Date.now();
    const counters = this.counters();
    const info = this.levelInfo;
    const rank = rankFor(info.level, RANKS);

    const group = (cadence: Cadence, heading: string): JournalChallengeGroup => {
      const window = profile.challenges[cadence];
      return {
        heading,
        meta: `${JOURNAL_COPY.resetsIn} ${formatDuration(
          windowEndsAt(cadence, now, WEEK_STARTS_ON) - now,
        )}`,
        rows: window
          ? progressOf(window, CHALLENGES, counters).map((p) => ({
              id: p.def.id,
              title: p.def.title,
              titleAr: p.def.titleAr,
              value: p.value,
              target: p.def.target,
              xp: p.def.xp,
              done: p.done,
            }))
          : [],
      };
    };

    const copy = JOURNAL_COPY.collection;
    const tabs = JOURNAL_COPY.tabs;
    const sections: CollectionSection[] = [
      {
        id: 'species',
        tab: tabs.collection,
        title: copy.species,
        layout: 'list',
        items: SPECIES_ORDER.map((id) => {
          const species = SPECIES[id];
          return {
            id,
            icon: speciesGlyph(species),
            name: species.name,
            sub: `${species.nameAr} · ${RARITY_LABEL[species.rarity]}`,
            earned: speciesGrown.has(id),
          };
        }),
      },
      {
        id: 'marks',
        tab: tabs.collection,
        title: copy.marks,
        layout: 'list',
        items: WEATHER_ORDER.flatMap((weatherId) => {
          const id = WEATHER[weatherId].mutation;
          if (!id) return [];
          const mutation = MUTATIONS[id];
          return [
            {
              id,
              icon: '<span class="collect-dot"></span>',
              name: mutation.name,
              sub: `×${mutation.bonus}`,
              detail: mutation.detail,
              tint: hex(mutation.tint),
              earned: mutations.has(id),
            },
          ];
        }),
      },
      {
        id: 'facts',
        tab: tabs.collection,
        title: copy.facts,
        layout: 'list',
        items: FACTS.map((fact) => {
          const seen = factsSeen.has(fact.id);
          return {
            id: fact.id,
            icon: seen ? '📜' : '🔒',
            name: seen ? fact.headline : copy.lockedFact,
            sub: CATEGORY_LABEL[fact.category],
            earned: seen,
            open: () => {
              ui.closeJournal();
              ui.showFact(fact, this.host.factsSeen().size, TOTAL_FACTS);
            },
          };
        }),
      },
      {
        id: 'badges',
        tab: tabs.badges,
        title: tabs.badges,
        layout: 'grid',
        items: BADGES.map((badge) => ({
          id: badge.id,
          icon: badge.glyph,
          name: badge.name,
          detail: badge.detail,
          earned: badges.has(badge.id),
        })),
      },
    ];

    return {
      kicker: JOURNAL_COPY.kicker,
      xpLabel: JOURNAL_COPY.xp,
      level: {
        level: info.level,
        rank: rank.name,
        rankAr: rank.nameAr,
        xp: info.xp,
        floor: info.floor,
        next: info.next,
        progress: info.progress,
        note:
          info.next === null
            ? JOURNAL_COPY.maxLevel
            : JOURNAL_COPY.levelReward(LEVEL_REWARD_SEEDLINGS),
      },
      challengesTab: tabs.challenges,
      challenges: [
        group('daily', JOURNAL_COPY.daily),
        group('weekly', JOURNAL_COPY.weekly),
      ],
      emptyChallenges: JOURNAL_COPY.noChallenges,
      sections,
    };
  }
}
